import { useEffect, useRef, useState } from 'react';
import type { OrderDto } from '@tikkas/shared';
import { ART, COIN, IC, Svg } from '../art/icons.js';
import { burst, PALETTE, POINTER, wheelSvg } from '../art/bowl.js';
import { api } from '../lib/api.js';
import { num, readyWhen, reduced, vibrate } from '../lib/format.js';
import { useStore } from '../lib/store.js';

function coinRain(n: number): string {
  let s = '';
  for (let i = 0; i < n; i++) s += `<i style="--x:${Math.round(Math.random() * 90 + 2)}%;--d:${(Math.random() * 0.7).toFixed(2)}s;--t:${(1.1 + Math.random() * 0.6).toFixed(2)}s;--h:${Math.round(700 + Math.random() * 200)}px">${COIN(22 + Math.round(Math.random() * 10))}</i>`;
  return `<div class="rain" aria-hidden="true">${s}</div>`;
}

/** "Order placed" splash. */
export function PlacedFx({ order, onDone }: { order: OrderDto; onDone: () => void }) {
  const [b] = useState(() => burst(36, PALETTE));
  useEffect(() => {
    const t = setTimeout(onDone, reduced() ? 900 : 2300);
    return () => clearTimeout(t);
  }, [onDone]);
  return (
    <div id="fx">
      <div className="fx placed" onClick={onDone}>
        <Svg html={b} />
        <svg className="tickdraw" width="96" height="96" viewBox="0 0 80 80" aria-hidden="true"><circle cx="40" cy="40" r="34" fill="none" stroke="#ffd84d" strokeWidth="6" /><path d="M25 41l10 10 20-22" fill="none" stroke="currentColor" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" /></svg>
        <h2 className="rise" style={{ ['--d' as string]: '.45s' }}>Order placed</h2>
        <p className="rise" style={{ ['--d' as string]: '.6s' }}>#{order.number} · ready {readyWhen(order.readyAt)}<br />We'll tell you the moment it's ready.</p>
        <span className="skip">Tap to continue</span>
      </div>
    </div>
  );
}

/** Points land on collection. */
export function CollectFx({ from, to, earn, spinBonus, number, unlocked, onDone }: { from: number; to: number; earn: number; spinBonus: number; number: number; unlocked: boolean; onDone: () => void }) {
  const [shown, setShown] = useState(from);
  const [rain] = useState(() => coinRain(18));
  useEffect(() => {
    const t0 = performance.now(), dur = reduced() ? 1 : 1300;
    let raf = 0;
    const step = (t: number) => {
      const k = Math.min(1, (t - t0) / dur), e = 1 - Math.pow(1 - k, 3);
      setShown(Math.round(from + (to - from) * e));
      if (k < 1) raf = requestAnimationFrame(step);
    };
    const st = setTimeout(() => (raf = requestAnimationFrame(step)), 450);
    return () => {
      clearTimeout(st);
      cancelAnimationFrame(raf);
    };
  }, [from, to]);
  return (
    <div id="fx">
      <div className="fx" onClick={onDone}>
        <Svg html={rain} />
        <span className="dropin"><Svg html={COIN(76)} /></span>
        <h2 className="tnum">{num(shown)}</h2>
        <p style={{ marginTop: -6 }}>points</p>
        <p className="rise" style={{ ['--d' as string]: '.5s' }}><b>+{num(earn)} points</b> for order #{number}</p>
        {spinBonus > 0 && <p className="rise" style={{ ['--d' as string]: '.6s' }}><b>+{num(spinBonus)}</b> from your free spin</p>}
        {unlocked && <div className="unlock" style={{ ['--d' as string]: '1.4s' }}><Svg html={COIN(34)} /><span className="col" style={{ gap: 2 }}><b>Free main unlocked</b><span style={{ fontSize: 12.5 }}>Add it at checkout or ask at the till</span></span></div>}
        <span className="skip">Tap to continue</span>
      </div>
    </div>
  );
}

/** "You've won a free spin" arrives as a sheet over the order screen. */
export function SpinReveal({ order, onSpin, onLater }: { order: OrderDto; onSpin: () => void; onLater: () => void }) {
  return (
    <div id="fx">
      <div className="fx spinsheet" onClick={onLater}>
        <div className="spanel" role="dialog" aria-label="You've won a free spin" onClick={(e) => e.stopPropagation()}>
          <span className="grab" aria-hidden="true" />
          <span className="spin-eyebrow">A little thank you</span>
          <h2>You've won a free spin</h2>
          <p className="lede">Every spin wins points or free food.</p>
          <div className="ticketwrap" style={{ animationDelay: '.2s' }}>
            <div className="ticket2">
              <span className="stub"><span className="coinspin"><Svg html={COIN(56)} /></span></span>
              <span className="perf2" />
              <span className="body"><span className="k">Admit one</span><span className="t">Free spin</span><span className="m">Order #{order.number}</span></span>
              <span className="shine" />
            </div>
          </div>
          <button type="button" className="spinbtn" onClick={onSpin}>Spin now</button>
          <div className="spinlinks"><button type="button" onClick={onLater}>Later</button></div>
        </div>
      </div>
    </div>
  );
}

function spinEase(t: number): number {
  const k = 0.07, A = (2 * k) / (1 + k);
  if (t < k) return A * (t / k) * (t / k);
  const u = (t - k) / (1 - k);
  return A + (1 - A) * (1 - Math.pow(1 - u, 4));
}

/** The wheel. The server picks the segment; the phone only animates to it. */
export function Wheel({ order, onDone, onRules }: { order: OrderDto; onDone: (prize: { kind: 'points'; points: number } | { kind: 'dessert' }, bonusPoints: number) => void; onRules: () => void }) {
  const s = useStore();
  const wheel = s.config?.spinWheel ?? [];
  const SEG = 360 / Math.max(1, wheel.length);
  const [html] = useState(() => wheelSvg(wheel));
  const [state, setState] = useState<'idle' | 'spinning' | 'won'>('idle');
  const [prize, setPrize] = useState<{ kind: 'points'; points: number } | { kind: 'dessert' } | null>(null);
  const [shownPts, setShownPts] = useState(0);
  const [b, setB] = useState('');
  const spinner = useRef<HTMLDivElement>(null);
  const pointer = useRef<HTMLDivElement>(null);
  const angle = useRef(0);

  const go = async () => {
    if (state !== 'idle') return;
    setState('spinning');
    let r: { segment: number; prize: { kind: 'points'; points: number } | { kind: 'dessert' } };
    try {
      r = await api(`/api/orders/${order.id}/spin`, { body: {} });
    } catch (e) {
      s.toast(e instanceof Error ? e.message : 'Spin failed');
      setState('idle');
      return;
    }
    const i = r.segment, jitter = (Math.random() - 0.5) * 6, dur = reduced() ? 900 : 5600;
    const from = angle.current, restOn = 360 - (i * SEG + SEG / 2 + jitter);
    const to = from - (from % 360) + 360 * (reduced() ? 2 : 6) + restOn;
    const t0 = performance.now();
    let lastIdx: number | null = null, lastTick = 0;
    const frame = (now: number) => {
      const t = Math.min(1, (now - t0) / dur);
      const a = from + (to - from) * spinEase(t);
      if (spinner.current) spinner.current.style.transform = `rotate(${a.toFixed(3)}deg)`;
      const idx = Math.floor(((((360 - (a % 360)) % 360) + 360) % 360) / SEG);
      if (lastIdx !== null && idx !== lastIdx && now - lastTick > 45 && pointer.current) {
        lastTick = now;
        pointer.current.classList.remove('tick');
        void pointer.current.getBoundingClientRect();
        pointer.current.classList.add('tick');
        vibrate(3);
      }
      lastIdx = idx;
      if (t < 1) requestAnimationFrame(frame);
      else {
        angle.current = to;
        setTimeout(() => {
          setState('won');
          setPrize(r.prize);
          setB(burst(44, ['#ffd84d', '#f5b400', '#d63b2f', '#f08a24', '#7fb03f', '#ffffff']));
          vibrate([20, 40, 30]);
          const seg = spinner.current?.querySelector(`.seg[data-i="${i}"]`);
          seg?.classList.add('win');
          if (r.prize.kind === 'points') {
            const p0 = performance.now(), d2 = reduced() ? 1 : 900, n = r.prize.points;
            const step = (tt: number) => {
              const k = Math.min(1, (tt - p0) / d2), e = 1 - Math.pow(1 - k, 3);
              setShownPts(Math.round(n * e));
              if (k < 1) requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
          }
        }, 250);
      }
    };
    requestAnimationFrame(frame);
  };

  return (
    <div id="fx">
      <div className={`fx spinfx`}>
        <div className="rays" aria-hidden="true" />
        {b && <Svg html={b} />}
        <button type="button" className="round spinx" aria-label="Close" onClick={() => (state === 'won' && prize ? onDone(prize, prize.kind === 'points' ? prize.points : 0) : s.toast('Your free spin is saved on the order screen'))}><Svg html={IC.close} /></button>
        <span className="spin-eyebrow">Free spin · order #{order.number}</span>
        <h2 style={{ marginBottom: 18 }}>Every spin wins</h2>
        <div className={`wheelwrap ${state === 'spinning' ? 'spinning' : ''} ${state === 'won' ? 'won' : ''}`}>
          <div ref={pointer} dangerouslySetInnerHTML={{ __html: POINTER }} style={{ display: 'contents' }} />
          <div className="spinner" ref={spinner} dangerouslySetInnerHTML={{ __html: html }} />
        </div>
        <div className="col spinfoot">
          {state !== 'won' && <button type="button" className="spinbtn" disabled={state === 'spinning'} onClick={go}>{state === 'spinning' ? 'Good luck…' : 'Spin'}</button>}
          {state !== 'won' && <div className="spinlinks"><button type="button" onClick={onRules}>Rules</button></div>}
          {state === 'won' && prize && (
            <div className="prize">
              <span className="spin-eyebrow">You won</span>
              {prize.kind === 'dessert' ? <><span className="prizeart dropin"><Svg html={ART.gulab!} /></span><span className="amt2 word">A free dessert</span></> : <span className="amt2"><span>+{shownPts}</span><small>points</small></span>}
              <p>{prize.kind === 'dessert' ? 'Gulab Jamun or Rasgulla on us, saved in your rewards.' : `Added to your points when you collect #${order.number}.`}</p>
              <button type="button" className="spinbtn" style={{ marginTop: 8 }} onClick={() => onDone(prize, prize.kind === 'points' ? prize.points : 0)}>Brilliant</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
